import { memo, useCallback, useReducer } from 'react';

import { useFormik } from 'formik';
import { SquarePen, Trash2 } from 'lucide-react';
import { isShallowEqual, omit } from 'remeda';
import { toast } from 'sonner';
import useSWRMutation from 'swr/mutation';

import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';

import { options, UserGuideForm } from '../../form';

import css from '../UserGuidesTable.module.css';

import { userGuideService } from 'v2/api/userGuideService';
import { Popconfirm } from 'v2/components/popconfirm/Popconfirm';
import { Select } from 'v2/components/select/Select';
import { UserGuideType } from 'v2/constants';
import { UserGuideModel } from 'v2/types/UserGuideModel';
import { handleHttpError } from 'v2/utils';

interface UserGuideActionsColumnProps {
  guide: UserGuideModel;
  onRevalidate: VoidFunction;
}

export const UserGuideActionsColumn = memo<UserGuideActionsColumnProps>(({ guide, onRevalidate }) => {
  const [isOpen, setOpen] = useReducer((state) => !state, false);

  const { trigger: triggerDelete } = useSWRMutation(['deleteGuide', guide.id], userGuideService.deleteGuide, {
    onSuccess: () => {
      onRevalidate();
    },
    onError: handleHttpError,
  });

  const { trigger: triggerUpdate, isMutating } = useSWRMutation(
    ['updateGuide', guide.id],
    userGuideService.updateGuide,
    {
      onSuccess: () => {
        onRevalidate();
        toast.success('The guide has been successfully updated');
        handleClose();
      },
      onError: handleHttpError,
    }
  );

  const formik = useFormik<UserGuideForm>({
    initialValues: {
      key: guide.key,
      type: guide.type,
      reward: guide.reward,
      isDefault: guide.isDefault,
    },
    onSubmit: (values) => {
      if (isShallowEqual(values, omit(guide, ['id']))) {
        handleClose();
      } else {
        triggerUpdate(values);
      }
    },
  });

  const handleChange = useCallback((type: UserGuideType) => {
    formik.setFieldValue('type', type);
  }, []);

  const handleClose = () => {
    setOpen();
    formik.resetForm();
  };

  return (
    <div className={css.userGuideTableActions}>
      <Tooltip title="Edit" placement="top">
        <IconButton color="warning" onClick={setOpen}>
          <SquarePen width={20} height={20} />
        </IconButton>
      </Tooltip>

      <Popconfirm message="Are you sure that you want to delete this guide?" onConfirm={triggerDelete}>
        <Tooltip title="Delete" placement="top">
          <IconButton color="error">
            <Trash2 width={20} height={20} />
          </IconButton>
        </Tooltip>
      </Popconfirm>

      <Dialog open={isOpen} onClose={setOpen} maxWidth="xs" fullWidth>
        <DialogTitle>Edit User Guide</DialogTitle>

        <form onSubmit={formik.handleSubmit}>
          <DialogContent className="form">
            <TextField
              name="key"
              value={formik.values.key}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              label="Guide Key"
              required
              fullWidth
            />

            <Select value={formik.values.type} options={options} label="Guide Type" onChange={handleChange} required />

            <TextField
              name="reward"
              value={formik.values.reward}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              label="Reward"
              type="number"
              required
              fullWidth
            />
          </DialogContent>

          <DialogActions>
            <Button onClick={handleClose} color="error" variant="outlined">
              Cancel
            </Button>

            <Button type="submit" variant="contained" loading={isMutating}>
              Edit
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </div>
  );
});

UserGuideActionsColumn.displayName = 'UserGuideActionsColumn';
